import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { getGeoQueriesCatalog, patchGeoQuery, unwrapGeoPayload } from '../../../api';
import { compareGeoQueryIds } from '../utils/geoPresentation';

function buildDraftMap(prompts) {
  const map = {};
  for (const row of prompts) {
    map[String(row.id)] = Boolean(row.active);
  }
  return map;
}

export default function GeoEditPromptsModal({ isOpen, onClose, onSaved }) {
  const [prompts, setPrompts] = useState([]);
  const [draftActive, setDraftActive] = useState({});
  const [initialActive, setInitialActive] = useState({});
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [showLoadingMessage, setShowLoadingMessage] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [openKey, setOpenKey] = useState(0);
  const [listKey, setListKey] = useState(0);
  const promptsRef = useRef([]);

  useEffect(() => {
    promptsRef.current = prompts;
  }, [prompts]);

  const loadPrompts = useCallback(async ({ background = false } = {}) => {
    if (!background) setLoading(true);
    if (!background && promptsRef.current.length === 0) setError(null);
    try {
      const response = await getGeoQueriesCatalog();
      const payload = unwrapGeoPayload(response);
      const rows = Array.isArray(payload) ? payload : [];
      const sorted = [...rows].sort((a, b) => compareGeoQueryIds(a.id, b.id));
      setPrompts(sorted);
      const draft = buildDraftMap(sorted);
      setDraftActive(draft);
      setInitialActive(draft);
      setError(null);
    } catch (err) {
      const message = err?.message || 'Failed to load prompts';
      setError(message);
      if (!background || promptsRef.current.length === 0) {
        setPrompts([]);
        setDraftActive({});
        setInitialActive({});
      }
    } finally {
      if (!background) setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isOpen) return undefined;
    loadPrompts({ background: promptsRef.current.length > 0 });
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen, loadPrompts]);

  useEffect(() => {
    if (!loading) {
      setShowLoadingMessage(false);
      return undefined;
    }
    const timer = window.setTimeout(() => setShowLoadingMessage(true), 200);
    return () => window.clearTimeout(timer);
  }, [loading]);

  useEffect(() => {
    if (!isOpen) {
      setSearch('');
      setError(null);
      return;
    }
    setOpenKey((key) => key + 1);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || loading || prompts.length === 0) return;
    setListKey((key) => key + 1);
  }, [isOpen, loading, prompts.length]);

  const filteredPrompts = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return prompts;
    return prompts.filter((row) => (
      String(row.id).includes(q)
      || String(row.label || '').toLowerCase().includes(q)
    ));
  }, [prompts, search]);

  const changedIds = useMemo(
    () => Object.keys(draftActive).filter(
      (id) => draftActive[id] !== initialActive[id],
    ),
    [draftActive, initialActive],
  );

  const activeCount = useMemo(
    () => Object.values(draftActive).filter(Boolean).length,
    [draftActive],
  );

  const toggleActive = (id, checked) => {
    setDraftActive((prev) => ({ ...prev, [String(id)]: checked }));
  };

  const setAllActive = (active) => {
    setDraftActive((prev) => {
      const next = { ...prev };
      for (const row of prompts) {
        next[String(row.id)] = active;
      }
      return next;
    });
  };

  const handleBackdropMouseDown = (event) => {
    if (event.target === event.currentTarget && !saving) {
      onClose();
    }
  };

  const handleSave = async () => {
    if (!changedIds.length || saving) return;
    setSaving(true);
    setError(null);
    try {
      await Promise.all(
        changedIds.map((id) => patchGeoQuery(id, { active: draftActive[id] })),
      );
      onSaved?.();
      onClose();
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Failed to save prompts');
    } finally {
      setSaving(false);
    }
  };

  const isInitialLoad = loading && prompts.length === 0;
  const showListAnimation = !isInitialLoad && filteredPrompts.length > 0;

  const listBody = (() => {
    if (isInitialLoad && showLoadingMessage) {
      return <p className="assistants-status">Loading prompts…</p>;
    }
    if (error && prompts.length === 0 && !loading) {
      return <p className="assistants-status error" role="alert">{error}</p>;
    }
    if (filteredPrompts.length === 0) {
      if (prompts.length > 0) {
        return <p className="assistants-status">No prompts match your search.</p>;
      }
      return null;
    }
    return filteredPrompts.map((row, index) => {
      const id = String(row.id);
      const isActive = Boolean(draftActive[id]);
      return (
        <label
          key={id}
          className={`competitor-item ${isActive ? 'selected' : ''}`}
          style={showListAnimation ? { '--table-row-delay': index } : undefined}
        >
          <input
            type="checkbox"
            checked={isActive}
            onChange={(e) => toggleActive(id, e.target.checked)}
          />
          <span className="competitor-meta">
            <span className="competitor-name">{row.label}</span>
            <span className="competitor-domain">
              Prompt #
              {id}
            </span>
          </span>
        </label>
      );
    });
  })();

  if (!isOpen) return null;

  return createPortal(
    <div
      key={openKey}
      className="configure-dialog-backdrop geo-prompt-edit-backdrop"
      onMouseDown={handleBackdropMouseDown}
    >
      <div
        className="configure-dialog configure-dialog--geo-prompts"
        role="dialog"
        aria-modal="true"
        aria-labelledby="geo-edit-prompts-title"
      >
        <div className="p-dialog-header">
          <span className="p-dialog-title" id="geo-edit-prompts-title">Edit prompts</span>
          <button
            type="button"
            className="drawer-close filters-drawer-close"
            onClick={onClose}
            disabled={saving}
            aria-label="Close"
          >
            ×
          </button>
        </div>
        <div className="p-dialog-content">
          <p className="modal-desc">
            Choose which prompts are active for Prompt Pulse runs and insights. Inactive prompts are
            excluded from scheduled runs.
          </p>

          <div className="geo-prompt-edit-toolbar">
            <input
              type="search"
              className="geo-prompt-edit-search"
              placeholder="Search by prompt id or text…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <div className="geo-prompt-edit-toolbar__actions">
              <button
                type="button"
                className="btn btn-sm filters-drawer-btn-reset"
                onClick={() => setAllActive(true)}
                disabled={isInitialLoad || prompts.length === 0}
              >
                Enable All
              </button>
              <button
                type="button"
                className="btn btn-sm filters-drawer-btn-reset"
                onClick={() => setAllActive(false)}
                disabled={isInitialLoad || prompts.length === 0}
              >
                Disable All
              </button>
            </div>
          </div>

          <p className="config-hint">
            {activeCount}
            {' '}
            of
            {' '}
            {prompts.length}
            {' '}
            prompts active
            {changedIds.length > 0 ? ` · ${changedIds.length} unsaved change${changedIds.length === 1 ? '' : 's'}` : ''}
          </p>

          <div className="competitor-picker">
            <section className="competitor-column geo-prompt-edit-column">
              <div
                key={listKey}
                className={`competitor-list${isInitialLoad ? ' geo-prompt-edit-list--loading' : ''}${showListAnimation ? ' geo-prompt-edit-list--entered' : ''}`}
              >
                {listBody}
              </div>
            </section>
          </div>

          {error && prompts.length > 0 && !saving ? (
            <p className="assistants-status error" role="alert">{error}</p>
          ) : null}

          {saving && error ? (
            <p className="assistants-status error" role="alert">{error}</p>
          ) : null}
        </div>
        <div className="p-dialog-footer">
          <button
            type="button"
            className="cancel-btn"
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </button>
          <button
            type="button"
            className="add-btn"
            onClick={handleSave}
            disabled={saving || isInitialLoad || changedIds.length === 0}
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
